import { useState } from 'react';
import { API_URL, postData } from '../../Api';
import { PanelBox, Title } from '../../components/Display';
import { Button } from '../../components/Form';
import { LeftComponents, RightComponents, Row } from '../../components/Layout';
import {
    TableBody,
    TableColumn,
    TableData,
    TableHeader,
    TableRow,
} from '../../components/Table';

const EXAMPLES = [
    'در ۳۰ روز گذشته، ۱۰ فروشگاه برتر بر اساس تعداد سفارش کدام‌اند؟',
    'توزیع سفارش‌های ماه گذشته به تفکیک وضعیت سفارش چطور بوده؟',
    'سهم هر روش پرداخت از تعداد سفارش‌ها و مبلغ، در سه ماه اخیر',
    '۱۰ استان با بیشترین سفارش در ۹۰ روز گذشته به همراه میانگین وزن مرسوله',
    'نرخ برگشتی هر فروشگاه در ماه گذشته (فقط فروشگاه‌های با بیش از ۱۰۰ سفارش)',
    'فروشگاه‌هایی که تراز حساب منفی دارند، از بیشترین بدهی',
];

// Numbers arrive as JSON numbers; render them with Persian digits and thousand
// separators, but leave text and dates exactly as the query produced them.
const formatCell = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'number') return value.toLocaleString('fa-IR', { maximumFractionDigits: 2 });
    if (typeof value === 'boolean') return value ? 'بله' : 'خیر';
    return String(value);
};

export default function TaroffAiAnalyst() {
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [showSql, setShowSql] = useState(false);
    const [showSteps, setShowSteps] = useState(false);
    const [history, setHistory] = useState([]);
    const [tableSize, setTableSize] = useState(20);
    const [tablePage, setTablePage] = useState(1);

    const ask = (text) => {
        const asked = (text ?? question).trim();
        if (!asked || loading) return;
        setQuestion(asked);
        setLoading(true);
        setError(null);
        setResult(null);
        setShowSql(false);
        setShowSteps(false);
        setTablePage(1);
        postData('/api/panel/page/taroff-ai-analyst/ask', { question: asked, history })
            .then((response) => {
                if (response.status !== 'success') {
                    setError(response.message || 'خطای نامشخص');
                    return;
                }
                setResult(response.data);
                // Keep the last few turns so follow-ups like «همین را برای ماه قبل بده» work.
                setHistory((previous) =>
                    [...previous, { question: asked, sql: response.data.sql }].slice(-4)
                );
            })
            .catch((e) => {
                setError(e?.message || e?.detail || 'ارتباط با سرور برقرار نشد.');
            })
            .finally(() => setLoading(false));
    };

    const downloadExcel = () => {
        if (!result?.export_key) return;
        const queryString = {
            token: localStorage.getItem('admin_auth_token'),
            key: result.export_key,
        };
        window.open(
            API_URL +
                '/api/panel/page/taroff-ai-analyst/export-excel?' +
                new URLSearchParams(queryString).toString(),
            '_blank'
        );
    };

    const startOver = () => {
        setHistory([]);
        setResult(null);
        setError(null);
        setQuestion('');
    };

    const rows = result?.rows || [];
    const pagedRows = rows.slice((tablePage - 1) * tableSize, tablePage * tableSize);
    const gotoPage = (page, size = undefined) => {
        setTablePage(page);
        size && setTableSize(size);
    };

    return (
        <PanelBox>
            <Row>
                <RightComponents>
                    <Title>گزارش‌ساز هوشمند</Title>
                </RightComponents>
                <LeftComponents>
                    {history.length > 0 && (
                        <Button onClick={startOver} color={'secondary'} rightIcon={'fal fa-rotate-left'}>
                            گفتگوی جدید
                        </Button>
                    )}
                </LeftComponents>
            </Row>

            <p className="text-sm text-gray-600 dark:text-gray-400">
                سؤالت را به فارسی و ساده بپرس؛ هوش مصنوعی روی داده‌های سامانه کوئری می‌زند و نتیجه را
                به صورت جدول همراه با یک توضیح کوتاه نشان می‌دهد. دسترسی آن فقط خواندنی و محدود به
                داده‌های کسب‌وکار است.
            </p>

            {/* ------------------------------------------------------------ ask */}
            <div className="flex flex-col gap-2">
                <textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) ask();
                    }}
                    rows={3}
                    disabled={loading}
                    placeholder="مثلاً: در ۳۰ روز گذشته کدام فروشگاه‌ها بیشترین سفارش برگشتی را داشته‌اند؟"
                    className="w-full rounded-lg border-0 py-2.5 px-3 text-gray-900 dark:text-gray-200 bg-white dark:bg-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 text-sm leading-7 disabled:opacity-60"
                />
                <Row>
                    <RightComponents>
                        <span className="text-xs text-gray-400 self-center">
                            برای ارسال، Ctrl+Enter را بزن
                        </span>
                    </RightComponents>
                    <LeftComponents>
                        <Button onClick={() => ask()} disabled={loading || !question.trim()} rightIcon={'fal fa-sparkles'}>
                            {loading ? 'در حال تحلیل...' : 'بپرس'}
                        </Button>
                    </LeftComponents>
                </Row>
            </div>

            {!result && !loading && (
                <div className="flex flex-col gap-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">چند نمونه سؤال:</span>
                    <div className="flex flex-wrap gap-2">
                        {EXAMPLES.map((example) => (
                            <button
                                key={example}
                                onClick={() => ask(example)}
                                className="rounded-full px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 text-right"
                            >
                                {example}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {loading && (
                <div className="flex items-center gap-3 rounded-lg bg-blue-50 dark:bg-blue-950 p-4 text-sm text-blue-800 dark:text-blue-200">
                    <i className="fal fa-spinner-third fa-spin fa-lg"></i>
                    <span>
                        در حال بررسی داده‌ها... سؤال‌های ساده حدود ۱۰ ثانیه طول می‌کشند؛ برای
                        سؤال‌های تحلیلی ممکن است چند کوئری پشت‌سرهم لازم باشد و تا حدود دو دقیقه طول بکشد.
                    </span>
                </div>
            )}

            {error && (
                <div className="rounded-lg bg-red-50 dark:bg-red-950 p-4 text-sm text-red-800 dark:text-red-200 whitespace-pre-wrap">
                    {error}
                </div>
            )}

            {/* --------------------------------------------------------- result */}
            {result && (
                <div className="flex flex-col gap-4">
                    {result.summary && (
                        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/50 ring-1 ring-inset ring-emerald-200 dark:ring-emerald-900 p-4">
                            <div className="flex items-center gap-2 mb-2 text-emerald-900 dark:text-emerald-200 font-semibold text-sm">
                                <i className="fal fa-sparkles"></i>
                                <span>{result.title || 'خلاصه گزارش'}</span>
                            </div>
                            <p className="text-sm leading-8 text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                                {result.summary}
                            </p>
                        </div>
                    )}

                    {result.assumptions && (
                        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/40 p-3 text-xs leading-6 text-amber-900 dark:text-amber-200">
                            <strong>فرض‌های در نظر گرفته‌شده: </strong>
                            {result.assumptions}
                        </div>
                    )}

                    <Row>
                        <RightComponents>
                            <span className="text-sm text-gray-600 dark:text-gray-400 self-center">
                                {result.row_count.toLocaleString('fa-IR')} ردیف
                                {result.truncated && ' (محدود شده به سقف مجاز)'}
                            </span>
                        </RightComponents>
                        <LeftComponents>
                            {result.step_count > 1 && (
                                <Button onClick={() => setShowSteps(!showSteps)} color={'secondary'} rightIcon={'fal fa-list-ol'}>
                                    {showSteps ? 'پنهان کردن مراحل' : `مراحل (${result.step_count})`}
                                </Button>
                            )}
                            <Button onClick={() => setShowSql(!showSql)} color={'secondary'} rightIcon={'fal fa-code'}>
                                {showSql ? 'پنهان کردن کوئری' : 'نمایش کوئری'}
                            </Button>
                            <Button onClick={downloadExcel} color={'secondary'} rightIcon={'fal fa-file-excel'} disabled={!rows.length}>
                                اکسل
                            </Button>
                        </LeftComponents>
                    </Row>

                    {showSteps && (
                        <div className="flex flex-col gap-2">
                            {(result.steps || []).map((step) => (
                                <div
                                    key={step.step}
                                    className="rounded-lg bg-gray-50 dark:bg-gray-800/60 ring-1 ring-inset ring-gray-200 dark:ring-gray-700 p-3"
                                >
                                    <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
                                        <span className="rounded-full bg-gray-200 dark:bg-gray-700 px-2 py-0.5 text-gray-700 dark:text-gray-200">
                                            {step.step}
                                        </span>
                                        <span
                                            className={
                                                'rounded-full px-2 py-0.5 ' +
                                                (step.tool === 'deliver_report'
                                                    ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-800 dark:text-emerald-200'
                                                    : 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200')
                                            }
                                        >
                                            {step.tool === 'deliver_report' ? 'گزارش نهایی' : 'بررسی داده'}
                                        </span>
                                        {step.purpose && (
                                            <span className="text-gray-700 dark:text-gray-300">{step.purpose}</span>
                                        )}
                                        <span className="text-gray-400">
                                            {step.error ? 'خطا' : `${step.row_count?.toLocaleString('fa-IR')} ردیف`} · {step.seconds}s
                                        </span>
                                    </div>
                                    {step.error && (
                                        <div className="text-xs text-red-700 dark:text-red-300 mb-2">{step.error}</div>
                                    )}
                                    <pre dir="ltr" className="overflow-x-auto rounded bg-gray-900 text-gray-100 p-2 text-[11px] leading-5 text-left">
                                        {step.sql}
                                    </pre>
                                </div>
                            ))}
                        </div>
                    )}

                    {showSql && (
                        <pre dir="ltr" className="overflow-x-auto rounded-lg bg-gray-900 text-gray-100 p-4 text-xs leading-6 text-left">
                            {result.sql}
                        </pre>
                    )}

                    {rows.length > 0 ? (
                        <TableData total={rows.length} size={tableSize} page={tablePage} setPage={gotoPage}>
                            <TableHeader>
                                <TableRow>
                                    {result.columns.map((column) => (
                                        <TableColumn key={column} mobile={false}>
                                            {column}
                                        </TableColumn>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pagedRows.map((row, rowIndex) => (
                                    <TableRow key={rowIndex}>
                                        {row.map((cell, cellIndex) => (
                                            <TableColumn key={cellIndex} mobile={false}>
                                                {formatCell(cell)}
                                            </TableColumn>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </TableData>
                    ) : (
                        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4 text-sm text-gray-600 dark:text-gray-400">
                            نتیجه‌ای برای این سؤال پیدا نشد.
                        </div>
                    )}

                    <div className="text-xs text-gray-400 dark:text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                        <span>مدل: {(result.models || []).join('، ')}</span>
                        <span>{result.step_count} کوئری</span>
                        <span>زمان: {result.elapsed_seconds} ثانیه</span>
                        <span>هزینه این گزارش: ${result.cost_usd}</span>
                    </div>
                </div>
            )}
        </PanelBox>
    );
}
